import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
interface EbookMockupProps {
  title?: string;
  subtitle?: string;
  className?: string;
  tilt?: number;
  delay?: number;
  image?: string;
}
export function EbookMockup({
  title = 'LA BIBLIA DE 500 PROMPTS',
  subtitle = 'EDICIÓN PROFESIONAL',
  className = '',
  tilt = -15,
  delay = 0,
  image
}: EbookMockupProps) {
  // ===== IMAGE-DRIVEN HERO PRODUCT SHOT =====
  // When an image is provided, we drop the fake CSS book wrapper (which
  // fights against a real photographed mockup) and present it as a clean,
  // Apple-style hero product shot with interactive parallax tilt,
  // cinematic gold ambient glow, light streak, and a soft floor reflection.
  if (image) {
    return <ProductShot image={image} title={title} className={className} delay={delay} data-id="element-34" />;
  }
  // ===== FALLBACK: CSS-RENDERED 3D BOOK (for bonuses without photos) =====
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 1,
    delay,
    ease: [0.16, 1, 0.3, 1]
  }} className={`relative perspective-1000 w-64 h-80 md:w-80 md:h-[26rem] ${className}`} data-id="element-35">
      <motion.div animate={{
      y: [0, -10, 0]
    }} transition={{
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: delay * 2
    }} className="w-full h-full preserve-3d relative" style={{
      transform: `rotateY(${tilt}deg) rotateX(5deg)`
    }} data-id="element-36">
        {/* Shadow underneath */}
        <div className="absolute -bottom-10 left-10 w-full h-10 bg-black/80 blur-2xl rounded-full transform rotate-x-90 translate-z-[-50px]" data-id="element-37"></div>

        {/* Spine */}
        <div className="absolute left-0 top-0 w-10 md:w-12 h-full bg-black-200 origin-left border-r border-gold/20 flex items-center justify-center overflow-hidden" style={{
        transform: 'rotateY(-90deg)'
      }} data-id="element-38">
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-50" data-id="element-39"></div>
          <span className="text-gold/80 text-[10px] md:text-xs rotate-[-90deg] whitespace-nowrap font-display tracking-[0.3em] uppercase" data-id="element-40">
            {title}
          </span>
        </div>

        {/* Cover */}
        <div className="absolute left-0 top-0 w-full h-full bg-gradient-to-br from-black-100 to-black-300 border border-gold/30 shadow-2xl p-6 md:p-8 flex flex-col justify-between overflow-hidden preserve-3d" data-id="element-41">
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-gold/10 to-transparent opacity-40 transform -translate-x-full animate-[shimmer_8s_infinite]" data-id="element-42"></div>
          <div className="absolute inset-0 opacity-[0.02] mix-blend-overlay" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")"
        }} data-id="element-43"></div>

          <div className="relative z-10" data-id="element-44">
            <div className="w-12 h-[1px] bg-gold/50 mb-4" data-id="element-45"></div>
            <p className="text-gold/70 text-[10px] md:text-xs tracking-[0.2em] mb-3 uppercase" data-id="element-46">
              {subtitle}
            </p>
            <h3 className="text-gold font-display text-2xl md:text-4xl leading-[1.1] uppercase" data-id="element-47">
              {title}
            </h3>
          </div>

          <div className="relative z-10 flex justify-between items-end" data-id="element-48">
            <div className="text-cream/40 text-[8px] tracking-widest uppercase" data-id="element-49">
              Vol. 1
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border border-gold/30 flex items-center justify-center backdrop-blur-sm" data-id="element-50">
              <div className="w-4 h-4 md:w-5 md:h-5 bg-gradient-to-br from-gold to-gold-dark rounded-full shadow-[0_0_10px_rgba(212,175,122,0.5)]" data-id="element-51"></div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
// =====================================================================
// ProductShot — premium "Apple product page" presentation for the
// uploaded photo-real book covers. Includes mouse parallax tilt,
// cinematic gold halo, animated light streak, and soft floor reflection.
// =====================================================================
function ProductShot({
  image,
  title,
  className,
  delay
}: {
  image: string;
  title: string;
  className?: string;
  delay: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  // Mouse position (raw)
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  // Spring-smoothed values for liquid feel
  const springConfig = {
    stiffness: 80,
    damping: 20,
    mass: 1
  };
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [10, -10]), springConfig);
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-12, 12]), springConfig);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };
  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
    setIsHovered(false);
  };
  return <motion.div ref={ref} onMouseMove={handleMouseMove} onMouseEnter={() => setIsHovered(true)} onMouseLeave={handleMouseLeave} initial={{
    opacity: 0,
    y: 40
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 1.2,
    delay,
    ease: [0.16, 1, 0.3, 1]
  }} className={`relative perspective-1000 flex items-center justify-center w-full ${className}`} style={{
    aspectRatio: '3 / 4'
  }} data-id="element-52">
      {/* Outer cinematic gold halo — much larger and softer */}
      <div className="absolute inset-0 -m-20 pointer-events-none" data-id="element-53">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(212,175,122,0.25)_0%,rgba(212,175,122,0.08)_30%,transparent_65%)] blur-2xl" data-id="element-54"></div>
      </div>

      {/* Secondary inner glow that pulses gently */}
      <motion.div animate={{
      opacity: [0.5, 0.8, 0.5]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute inset-0 -m-10 pointer-events-none" data-id="element-55">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(232,200,138,0.2)_0%,transparent_55%)] blur-xl" data-id="element-56"></div>
      </motion.div>

      {/* The floating, parallax product shot */}
      <motion.div animate={{
      y: [0, -12, 0]
    }} transition={{
      duration: 7,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="relative w-full h-full preserve-3d" style={{
      rotateX,
      rotateY,
      transformStyle: 'preserve-3d'
    }} data-id="element-57">
        {/* The image itself — full bleed, no cropping, no frame */}
        <motion.img src={image} alt={title} draggable={false} className="w-full h-full object-contain select-none relative z-10" style={{
        filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.7)) drop-shadow(0 10px 20px rgba(0,0,0,0.5)) drop-shadow(0 0 40px rgba(212,175,122,0.15))',
        transform: 'translateZ(40px)'
      }} data-id="element-58" />

        {/* Animated light streak across the cover */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-20" style={{
        transform: 'translateZ(50px)'
      }} data-id="element-59">
          <motion.div initial={{
          x: '-150%',
          skewX: -20
        }} animate={{
          x: '250%',
          skewX: -20
        }} transition={{
          duration: 4,
          repeat: Infinity,
          repeatDelay: 6,
          ease: 'easeInOut'
        }} className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-white/8 to-transparent mix-blend-overlay" data-id="element-60" />
        </div>

        {/* Specular highlight that follows the mouse on hover */}
        <motion.div animate={{
        opacity: isHovered ? 0.3 : 0
      }} transition={{
        duration: 0.4
      }} className="absolute inset-0 pointer-events-none z-20 mix-blend-overlay" style={{
        background: useTransform([mouseX, mouseY], ([x, y]: number[]) => `radial-gradient(circle at ${(x + 0.5) * 100}% ${(y + 0.5) * 100}%, rgba(255,240,210,0.5) 0%, transparent 40%)`) as any,
        transform: 'translateZ(45px)'
      }} data-id="element-61" />
      </motion.div>

      {/* Floor reflection — a flipped, faded version of the image */}
      <div className="absolute left-0 right-0 pointer-events-none overflow-hidden" style={{
      top: '88%',
      height: '40%',
      maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 60%)',
      WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 60%)'
    }} data-id="element-62">
        <img src={image} alt="" draggable={false} aria-hidden="true" className="w-full h-full object-contain select-none opacity-40" style={{
        transform: 'scaleY(-1)'
      }} data-id="element-63" />
      </div>

      {/* Subtle dark vignette at bottom for grounding */}
      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-8 bg-black blur-3xl opacity-80 pointer-events-none" data-id="element-64"></div>
    </motion.div>;
}